import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { fetchVerifiedOrders } from '../api/api'; 
// @ts-ignore
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit, FaTruck, FaTimesCircle, FaMapMarkerAlt, FaClock, FaInfoCircle, FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa'; 
import { useNavigate } from 'react-router-dom';

export interface Order {
    id: string;
    orderId?: string;
    waybill?: string; 
    customerName?: string;
    mobile?: string;
    medicine?: string;
    medicinePrice?: number;
    medicineQuantity?: number;
    payableAmount?: number;
    status?: string;
    sellerUsername?: string;
    mainUser?: {
        firstName: string;
        lastName?: string;
        mobile?: string;
    };
}

const VerifiedOrders: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isAgent, setIsAgent] = useState<boolean>(false);
    const [shippingOrderId, setShippingOrderId] = useState<string | null>(null); // Specific order shipping loader
    const navigate = useNavigate();

    // Tracking States
    const [trackModalOpen, setTrackModalOpen] = useState<boolean>(false);
    const [trackLoading, setTrackLoading] = useState<boolean>(false);
    const [trackData, setTrackData] = useState<any>(null);
    const [isShippingBulk, setIsShippingBulk] = useState<boolean>(false);

    // Role Check
    useEffect(() => {
        const checkUserRole = () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    }).join(''));
                    const payload = JSON.parse(jsonPayload);
                    if (JSON.stringify(payload).toUpperCase().includes('AGENT')) {
                        setIsAgent(true);
                    }
                }
            } catch (e) { console.error("Error decoding token", e); }
        };
        checkUserRole();
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await fetchVerifiedOrders();
            setOrders(response.data);
        } catch (err) {
            setError('Failed to fetch verified orders.');
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchOrders(); }, []);

    // 🔥 SHIP SINGLE ORDER (With Professional Popup for 402 Error)
    const handleShipOrder = async (orderId: string) => {
        setShippingOrderId(orderId); // Start loading for this row
        try {
            const token = localStorage.getItem('token');
            // const res = await fetch(`http://localhost:8484/api/admin/ship-order/${orderId}`, {
            const res = await fetch(`https://www.ayushvatika.com/api/admin/ship-order/${orderId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // 🛑 HANDLE INSUFFICIENT BALANCE (402)
            if (res.status === 402) {
                toast.error(
                    <div className="flex flex-col gap-1 p-1">
                        <span className="font-bold flex items-center gap-2 text-red-700">
                            <FaExclamationTriangle /> Delhivery Balance Low!
                        </span>
                        <p className="text-xs text-gray-700 leading-normal">
                            Aapka order place nahi ho saka kyunki Delhivery wallet me balance khatam ho gaya hai. 
                            Kripya <b>one.delhivery.com</b> par ja kar recharge karein.
                        </p>
                    </div>,
                    { position: "top-center", autoClose: 10000 }
                );
                return;
            }

            const data = await res.text();
            if (res.ok) {
                toast.success("Shipment Success! Order placed on Delhivery.");
                fetchOrders(); 
            } else {
                toast.error(`Error: ${data}`);
            }
        } catch (e) {
            toast.error("Server Connection Error. Please try again.");
        } finally {
            setShippingOrderId(null); // Stop loading
        }
    };

    const handleShipAll = async () => {
        const pending = orders.filter(o => !o.waybill);
        if (pending.length === 0) return toast.info("No orders to ship.");
        if (!window.confirm(`Ship all ${pending.length} orders?`)) return;
        setIsShippingBulk(true);
        for (const order of pending) { await handleShipOrder(order.id); }
        setIsShippingBulk(false);
    };

    const handleTrack = async (waybill?: string) => {
        if (!waybill) return toast.warning("Order not shipped yet.");
        setTrackModalOpen(true);
        setTrackLoading(true);
        try {
            const token = localStorage.getItem('token');
            // const response = await fetch('http://localhost:8484/api/delivery/track', { 
            const response = await fetch('https://www.ayushvatika.com/api/delivery/track', { 
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ waybill })
            });
            const data = await response.json();
            if (data.rawResponse) {
                const raw = JSON.parse(data.rawResponse);
                const statusObj = raw?.ShipmentData?.[0]?.Shipment?.Status || {};
                setTrackData({
                    waybill: waybill,
                    status: statusObj.Status || 'Manifested',
                    instructions: statusObj.Instructions || 'Processing at warehouse.',
                    location: statusObj.ScannedLocation || 'Warehouse',
                    time: statusObj.StatusDateTime || 'Recently Updated'
                });
            }
        } catch (err) { toast.error("Tracking error."); setTrackModalOpen(false); }
        finally { setTrackLoading(false); }
    };

    return (
        <AdminLayout>
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Verified Orders</h1>
                    {!isAgent && (
                        <button 
                            onClick={handleShipAll}
                            disabled={isShippingBulk || orders.filter(o => !o.waybill).length === 0}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-bold shadow-md flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                            {isShippingBulk ? "Shipping..." : <><FaPaperPlane /> Bulk Ship All</>}
                        </button>
                    )}
                </div>
                
                <div className="overflow-x-auto custom-scrollbar bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="py-3 px-4 text-left text-xs text-gray-500 uppercase">Order Details</th>
                                <th className="py-3 px-4 text-left text-xs text-gray-500 uppercase">Customer</th>
                                <th className="py-3 px-4 text-left text-xs text-gray-500 uppercase">Product</th>
                                <th className="py-3 px-4 text-right text-xs text-gray-500 uppercase">Amount</th>
                                <th className="py-3 px-4 text-center text-xs text-gray-500 uppercase">Delivery status</th>
                                <th className="py-3 px-4 text-center text-xs text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {orders.map((order) => {
                                const cName = order.mainUser ? `${order.mainUser.firstName} ${order.mainUser.lastName || ''}` : 'Unknown';
                                return (
                                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="py-3 px-4 text-sm">
                                            <div className="font-mono text-gray-600 dark:text-gray-400 font-bold">#{order.id.substring(0, 6)}</div>
                                            <div className="text-[10px] text-gray-400">Seller: {order.sellerUsername || 'Admin'}</div>
                                        </td>
                                        <td className="py-3 px-4 text-sm font-medium">
                                            <div className="text-gray-900 dark:text-white">{cName}</div>
                                            <div className="text-gray-500 text-xs">{order.mainUser?.mobile}</div>
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            <div className="text-gray-800 dark:text-gray-200">{order.medicine}</div>
                                            <div className="text-[10px] text-gray-500">Qty: {order.medicineQuantity}</div>
                                        </td>
                                        <td className="py-3 px-4 text-right font-bold text-green-600">₹{order.payableAmount?.toFixed(0)}</td>
                                        
                                        <td className="py-3 px-4 text-center">
                                            {order.waybill ? (
                                                <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-bold px-2 py-1 rounded border border-green-200 dark:border-green-800">
                                                    WB: {order.waybill}
                                                </span>
                                            ) : isAgent ? (
                                                <span className="text-gray-400 text-xs italic">Waiting for Admin</span>
                                            ) : (
                                                <button 
                                                    onClick={() => handleShipOrder(order.id)}
                                                    disabled={shippingOrderId === order.id}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold px-3 py-1.5 rounded shadow-sm transition-all flex items-center gap-1 mx-auto"
                                                >
                                                    {shippingOrderId === order.id ? "Processing..." : "Ship Now"}
                                                </button>
                                            )}
                                        </td>

                                        <td className="py-3 px-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleTrack(order.waybill)} disabled={!order.waybill} className={`p-2 rounded shadow-sm ${order.waybill ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                                                    <FaTruck size={14} />
                                                </button>
                                                {!isAgent && (
                                                    <button onClick={() => navigate(`/admin/update-order/${order.id}`)} className="p-2 bg-yellow-500 text-white rounded shadow-sm">
                                                        <FaEdit size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* TRACKING MODAL */}
            {trackModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
                        <div className="bg-indigo-600 p-4 flex justify-between text-white">
                            <h2 className="font-bold flex items-center gap-2"><FaTruck /> Live Track</h2>
                            <button onClick={() => setTrackModalOpen(false)}><FaTimesCircle size={20}/></button>
                        </div>
                        <div className="p-6">
                            {trackLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                    <p className="mt-4 text-sm text-gray-500">Checking with Delhivery...</p>
                                </div>
                            ) : trackData ? (
                                <div className="space-y-4">
                                    <div className="text-xl font-bold text-gray-900 dark:text-white border-b pb-2">{trackData.status}</div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{trackData.instructions}</p>
                                    <div className="text-xs text-gray-500 flex flex-col gap-1">
                                        <span className="flex items-center gap-1"><FaMapMarkerAlt /> {trackData.location}</span>
                                        <span className="flex items-center gap-1"><FaClock /> {trackData.time}</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default VerifiedOrders;